'use client';

import { formatCurrency } from '@/utils/format';

interface TotalSpendingTrendProps {
  totalThisMonth: number;
  totalLastMonth: number;
  totalLastMonthEqualDays: number;
  isPartialMonthScenario?: boolean;
  useEqualDayComparison?: boolean;
  onToggleEqualDayComparison?: () => void;
  currentMonthName?: string;
  previousMonthName?: string;
  isCompactLayout?: boolean; // true when on left side (CardSpendingComparison)
  onTotalClick?: (period: 'current' | 'previous') => void;
  /** When equal-day comparison: number of days being compared (e.g. 14) for helper text */
  daysSoFar?: number;
}

export function TotalSpendingTrend({
  totalThisMonth,
  totalLastMonth,
  totalLastMonthEqualDays,
  isPartialMonthScenario = false,
  useEqualDayComparison = false,
  onToggleEqualDayComparison,
  currentMonthName = 'This Month',
  previousMonthName = 'Last Month',
  isCompactLayout = false,
  onTotalClick,
  daysSoFar
}: TotalSpendingTrendProps) {

  if (!(totalThisMonth > 0 || totalLastMonth > 0 || totalLastMonthEqualDays > 0)) {
    return null;
  }

  if (isCompactLayout) {
    // Professional compact layout for left side (CardSpendingComparison)
    return (
      <div className="mt-3 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200/50 rounded-xl">
        <div className="space-y-2">
          {/* Header row with title and toggle */}
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-gray-900">Total Spending Trend</h4>
            {isPartialMonthScenario && onToggleEqualDayComparison && (
              <div className="flex flex-col gap-0.5">
                <div className="flex items-center gap-2">
                  <button
                    role="switch"
                    aria-checked={useEqualDayComparison}
                    aria-label={useEqualDayComparison
                      ? `Partial: comparing first ${daysSoFar ?? '?'} days of each period`
                      : `Full: comparing full ${previousMonthName} vs partial ${currentMonthName}`
                    }
                    onClick={onToggleEqualDayComparison}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-1 ${
                      useEqualDayComparison ? 'bg-blue-500' : 'bg-gray-300'
                    }`}
                    title={useEqualDayComparison
                      ? `Equal days: ${previousMonthName} (partial) vs ${currentMonthName} (partial)`
                      : `Full vs partial: ${previousMonthName} (full) vs ${currentMonthName} (partial)`
                    }
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                        useEqualDayComparison ? 'translate-x-4' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                  <span className="text-xs text-gray-600 font-medium">
                    {useEqualDayComparison ? 'Partial' : 'Full'}
                  </span>
                </div>
                {daysSoFar != null && useEqualDayComparison && (
                  <p className="text-[10px] text-gray-500">
                    Comparing first {daysSoFar} days of each period
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Data row with amounts and change */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs">
              <div
                className="cursor-pointer hover:bg-blue-50 rounded px-1.5 py-1 transition-colors"
                onClick={() => onTotalClick?.('current')}
                title="Click to view current period transactions"
              >
                <div className="text-gray-500 uppercase tracking-wide">{currentMonthName}</div>
                <div className="font-bold text-gray-900 transition-all duration-300">{formatCurrency(totalThisMonth)}</div>
              </div>
              <div className="w-px h-6 bg-gray-200" />
              <div
                className="cursor-pointer hover:bg-blue-50 rounded px-1.5 py-1 transition-colors"
                onClick={() => onTotalClick?.('previous')}
                title="Click to view previous period transactions"
              >
                <div className="text-gray-500 uppercase tracking-wide">{previousMonthName}</div>
                <div className="font-medium text-gray-600 transition-all duration-300">
                  {formatCurrency(useEqualDayComparison ? totalLastMonthEqualDays : totalLastMonth)}
                </div>
              </div>
            </div>

            {/* Compact change indicator */}
            <div className="flex items-center gap-1.5">
              {(() => {
                const previousAmount = useEqualDayComparison ? totalLastMonthEqualDays : totalLastMonth;
                const totalChange = previousAmount > 0 ? ((totalThisMonth - previousAmount) / previousAmount) * 100 : 0;
                const isIncrease = totalChange >= 0;
                const isZero = Math.abs(totalChange) < 0.1;

                return (
                  <>
                    {isZero ? (
                      <div className="flex items-center justify-center w-6 h-6 rounded-full bg-gray-100">
                        <span className="text-xs font-bold text-gray-600">–</span>
                      </div>
                    ) : (
                      <div className={`flex items-center justify-center w-6 h-6 rounded-full ${
                        isIncrease ? 'bg-red-100' : 'bg-green-100'
                      }`}>
                        {isIncrease ? (
                          <svg className="w-3 h-3 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M5.293 7.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L6.707 7.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                          </svg>
                        ) : (
                          <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M14.707 12.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 14.586V3a1 1 0 012 0v11.586l2.293-2.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                    )}
                    <div className="text-right">
                      <div className={`text-xs font-bold whitespace-nowrap ${
                        isZero ? 'text-gray-600' : isIncrease ? 'text-red-600' : 'text-green-600'
                      }`}>
                        {isZero ? '0%' : `${isIncrease ? '+' : ''}${totalChange.toFixed(1)}%`}
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Original horizontal layout for right side (MonthlySpendingChart)
  return (
    <div className="mt-3 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200/50 rounded-xl">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-1">
            <h4 className="text-sm font-semibold text-gray-900">
              Total Spending Trend
            </h4>
            {/* Partial Month Toggle - larger, clearer labels, ARIA */}
            {isPartialMonthScenario && onToggleEqualDayComparison && (
              <div className="flex flex-col gap-0.5">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">|</span>
                  <button
                    role="switch"
                    aria-checked={useEqualDayComparison}
                    aria-label={useEqualDayComparison
                      ? `Partial: comparing first ${daysSoFar ?? '?'} days of each period`
                      : `Full: comparing full ${previousMonthName} vs partial ${currentMonthName}`
                    }
                    onClick={onToggleEqualDayComparison}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-1 ${
                      useEqualDayComparison ? 'bg-blue-500' : 'bg-gray-300'
                    }`}
                    title={useEqualDayComparison
                      ? `Equal days: ${previousMonthName} (partial) vs ${currentMonthName} (partial)`
                      : `Full vs partial: ${previousMonthName} (full) vs ${currentMonthName} (partial)`
                    }
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                        useEqualDayComparison ? 'translate-x-4' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                  <span className="text-xs text-gray-600 font-medium">
                    {useEqualDayComparison ? 'Partial' : 'Full'}
                  </span>
                </div>
                {daysSoFar != null && useEqualDayComparison && (
                  <p className="text-[10px] text-gray-500 ml-1">
                    Comparing first {daysSoFar} days of each period
                  </p>
                )}
              </div>
            )}
          </div>
          <p className="text-xs text-gray-600">
            {isPartialMonthScenario && useEqualDayComparison
              ? (
                <>
                  Comparing same number of days:<br />
                  {previousMonthName} (partial) vs {currentMonthName} (partial)
                </>
              )
              : isPartialMonthScenario
                ? (
                  <>
                    Comparing {previousMonthName} (full month)<br />
                    vs {currentMonthName} (partial month)
                  </>
                )
                : 'Month-over-month comparison'
            }
          </p>
        </div>

        {/* This Month / Last Month Style Display */}
        <div className="flex items-center gap-3 text-sm">
          <div
            className="text-right cursor-pointer hover:bg-blue-50 rounded px-2 py-1 transition-colors"
            onClick={() => onTotalClick?.('current')}
            title="Click to view current period transactions"
          >
            <div className="text-xs text-gray-500 uppercase tracking-wide">{currentMonthName}</div>
            <div className="font-bold text-gray-900 transition-all duration-300">{formatCurrency(totalThisMonth)}</div>
          </div>
          <div className="w-px h-6 bg-gray-200" />
          <div
            className="text-right cursor-pointer hover:bg-blue-50 rounded px-2 py-1 transition-colors"
            onClick={() => onTotalClick?.('previous')}
            title="Click to view previous period transactions"
          >
            <div className="text-xs text-gray-500 uppercase tracking-wide">{previousMonthName}</div>
            <div className="font-medium text-gray-600 transition-all duration-300">
              {formatCurrency(useEqualDayComparison ? totalLastMonthEqualDays : totalLastMonth)}
            </div>
          </div>

          {/* Change Indicator */}
          <div className="flex items-center gap-2">
            {(() => {
              const previousAmount = useEqualDayComparison ? totalLastMonthEqualDays : totalLastMonth;
              const totalChange = previousAmount > 0 ? ((totalThisMonth - previousAmount) / previousAmount) * 100 : 0;
              const isIncrease = totalChange >= 0;
              const isZero = Math.abs(totalChange) < 0.1;

              return (
                <>
                  {isZero ? (
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100">
                      <span className="text-xs font-bold text-gray-600">–</span>
                    </div>
                  ) : (
                    <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                      isIncrease ? 'bg-red-100' : 'bg-green-100'
                    }`}>
                      {isIncrease ? (
                        <svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5.293 7.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L6.707 7.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M14.707 12.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 14.586V3a1 1 0 012 0v11.586l2.293-2.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  )}
                  <div className="text-right">
                    <div className={`text-xs font-bold ${
                      isZero ? 'text-gray-600' : isIncrease ? 'text-red-600' : 'text-green-600'
                    }`}>
                      {isZero ? '0%' : `${isIncrease ? '+' : ''}${totalChange.toFixed(1)}%`}
                    </div>
                    <div className="text-xs text-gray-500">vs last</div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
}